/**
 * Unilar Ofertas do Momento
 * Countdown (hours / min / sec) + mobile scroll progress bar.
 */

function parseMomentDate(value) {
  if (!value || !String(value).trim()) return null;

  const normalized = String(value).trim().replace(' ', 'T');
  const parsed = new Date(normalized).getTime();

  return Number.isNaN(parsed) ? null : parsed;
}

function padMomentUnit(value) {
  return String(value).padStart(2, '0');
}

class UnilarOfertasMomento {
  constructor(section) {
    this.section = section;
    this.countdownEl = section.querySelector('[data-countdown]');
    this.endDate = parseMomentDate(section.dataset.countdownEnd);
    this.track = section.querySelector('[data-moment-track]');
    this.dotsWrap = section.querySelector('[data-moment-dots]');
    this.countdownInterval = null;
    this.slides = [];
    this.dots = [];
    this.scrollRaf = 0;
    this.drag = {
      active: false,
      moved: false,
      startX: 0,
      scrollLeft: 0,
      pointerId: null,
    };
    this.init();
  }

  init() {
    this.initCountdown();
    this.initCarousel();
    this.initDrag();
  }

  initCountdown() {
    if (!this.countdownEl || !this.endDate) return;

    const hoursEl = this.countdownEl.querySelector('[data-countdown-hours]');
    const minutesEl = this.countdownEl.querySelector('[data-countdown-minutes]');
    const secondsEl = this.countdownEl.querySelector('[data-countdown-seconds]');

    const updateCountdown = () => {
      const distance = Math.max(0, this.endDate - Date.now());
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      if (hoursEl) hoursEl.textContent = String(hours);
      if (minutesEl) minutesEl.textContent = padMomentUnit(minutes);
      if (secondsEl) secondsEl.textContent = padMomentUnit(seconds);

      if (distance <= 0) this.stopCountdown();
    };

    updateCountdown();
    this.countdownInterval = setInterval(updateCountdown, 1000);
  }

  stopCountdown() {
    if (!this.countdownInterval) return;
    clearInterval(this.countdownInterval);
    this.countdownInterval = null;
  }

  initCarousel() {
    if (!this.track || !this.dotsWrap) return;

    this.slides = Array.from(this.track.querySelectorAll('.unilar-bf-moment__slide'));
    if (this.slides.length < 2) return;

    this.dotsWrap.hidden = false;
    this.dotsWrap.replaceChildren();
    this.dots = [];

    this.slides.forEach((slide, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'unilar-bf-moment__progress-seg';
      button.setAttribute('aria-label', `Ir para oferta ${index + 1}`);
      button.addEventListener('click', () => this.scrollToSlide(index));
      this.dotsWrap.appendChild(button);
      this.dots.push(button);
    });

    this.setActiveDot(0);
    this.track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.scrollRaf);
      this.scrollRaf = requestAnimationFrame(() => this.syncActiveDot());
    }, { passive: true });
  }

  getSlideScrollLeft(index) {
    const slide = this.slides[index];
    const first = this.slides[0];
    if (!slide || !first) return 0;
    return slide.offsetLeft - first.offsetLeft;
  }

  scrollToSlide(index) {
    if (!this.track) return;
    this.track.scrollTo({
      left: this.getSlideScrollLeft(index),
      behavior: 'smooth',
    });
    this.setActiveDot(index);
  }

  syncActiveDot() {
    if (!this.track || !this.slides.length) return;

    const paddingLeft = parseFloat(getComputedStyle(this.track).paddingLeft) || 0;
    const origin = this.track.getBoundingClientRect().left + paddingLeft;
    let activeIndex = 0;
    let closest = Number.POSITIVE_INFINITY;

    this.slides.forEach((slide, index) => {
      const distance = Math.abs(slide.getBoundingClientRect().left - origin);
      if (distance < closest) {
        closest = distance;
        activeIndex = index;
      }
    });

    this.setActiveDot(activeIndex);
  }

  initDrag() {
    if (!this.track) return;

    const interactiveSelector = 'a, button, input, label, textarea, select';

    this.track.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch') return;
      if (event.target instanceof Element && event.target.closest(interactiveSelector)) return;

      this.drag.active = true;
      this.drag.moved = false;
      this.drag.startX = event.clientX;
      this.drag.scrollLeft = this.track.scrollLeft;
      this.drag.pointerId = event.pointerId;
      this.track.classList.add('is-dragging');
      this.track.setPointerCapture(event.pointerId);
    });

    this.track.addEventListener('pointermove', (event) => {
      if (!this.drag.active) return;

      const delta = event.clientX - this.drag.startX;
      if (Math.abs(delta) > 4) this.drag.moved = true;
      this.track.scrollLeft = this.drag.scrollLeft - delta;
    });

    const stopDrag = () => {
      if (!this.drag.active) return;
      if (this.drag.pointerId != null) {
        try {
          this.track.releasePointerCapture(this.drag.pointerId);
        } catch (error) {
          // Capture may already be released.
        }
      }
      this.drag.active = false;
      this.drag.pointerId = null;
      this.track.classList.remove('is-dragging');
    };

    this.track.addEventListener('pointerup', stopDrag);
    this.track.addEventListener('pointercancel', stopDrag);
    this.track.addEventListener('click', (event) => {
      if (!this.drag.moved) return;
      event.preventDefault();
      event.stopPropagation();
      this.drag.moved = false;
    }, true);
  }

  setActiveDot(index) {
    this.dots.forEach((dot, dotIndex) => {
      dot.classList.toggle('is-active', dotIndex === index);
      if (dotIndex === index) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }
}

function initUnilarOfertasMomento() {
  document.querySelectorAll('[data-unilar-bf-moment]').forEach((section) => {
    if (!(section instanceof HTMLElement) || section.dataset.initialized === 'true') return;
    section.dataset.initialized = 'true';
    new UnilarOfertasMomento(section);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initUnilarOfertasMomento);
} else {
  initUnilarOfertasMomento();
}

document.addEventListener('shopify:section:load', initUnilarOfertasMomento);
